import { Fragment, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  Paper,
  Chip,
  FormControlLabel,
  Checkbox,
  Stack
} from "@material-ui/core";
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  PhotoLibrary as PhotoLibraryIcon
} from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import useFirestore from "../../../hooks/useFirestore";
import useWebotServer from "../../../hooks/useWebotServer";
import useAuth from "../../../hooks/useAuth";
import useCreativePreferences from "../../../hooks/useCreativePreferences";
import { Image } from "../../../components/Image";
import ListingPicker from "../signatures/ListingPicker";
import CreativeGeneratingBackgroundModal from "./CreativeGeneratingBackgroundModal";
import CreativeQualitySelector from "./CreativeQualitySelector";
import { fetchListingById } from "utils/listings/fetchListings";
import {
  templateDataType,
  templateInput,
  promptGenerationFinalParams,
  ColorAdditionalData,
  ListingImageAdditionalData,
  getRealtorLogoPrompt,
  getFixRealtorLogoPrompt
} from "../../../utils/creatives-generator/template_sections";
import { creativeTemplates } from "../../../utils/creatives-generator/creative_templates";
import type { ProcessedListingData, GeneratedUserCreative, MakeoverQuality } from "../../../@types/sharedSchema";
import { DEFAULT_CREATIVE_QUALITY } from "../../../@types/sharedSchema";
import type { ListingData } from "../signatures/ListingPicker";
import { storage } from "../../../firebaseConfig";
import i18n from "locales/i18n";

interface CreativesGeneratorModalProps {
  open: boolean;
  onClose: () => void;
  listingId?: string;
  editMode?: {
    imageUrl: string;
    creative: GeneratedUserCreative;
  };
  /** Called after a generation request resolves, so the parent can refresh the coin balance. */
  onGenerated?: () => void;
}

type GenerationStep = "template_selection" | "customization" | "generating" | "result";

interface TemplateInputValue {
  input: templateInput;
  value: string | boolean | ReturnType<typeof i18n.t>;
  defaultComputedValue?: string | boolean | ReturnType<typeof i18n.t>;
}

interface SelectedListings {
  [inputName: string]: ProcessedListingData | null;
}

function computeListingIds(
  selectedListings: SelectedListings,
  preloadedListingId?: string
): string[] {
  const ids = Object.values(selectedListings)
    .filter((l): l is ProcessedListingData => !!l)
    .map((l) => l.id)
    .filter((id): id is string => !!id);
  if (preloadedListingId && !ids.includes(preloadedListingId)) {
    ids.push(preloadedListingId);
  }
  return ids;
}

// Collect image URLs to send to the backend, honoring any include/exclude toggles
// (e.g. include_realtor_image gates whether realtor_image is included).
function collectImageUrls(templateInputs: TemplateInputValue[]): string[] {
  const includeRealtorImage = templateInputs.find(
    (item) => item.input.nameInPrompt === "include_realtor_image"
  );
  const isRealtorImageExcluded = includeRealtorImage?.value === false;

  return templateInputs
    .filter((item) => {
      if (item.input.type !== "image") return false;
      if (typeof item.value !== "string" || !item.value.trim()) return false;
      if (isRealtorImageExcluded && item.input.nameInPrompt === "realtor_image") return false;
      return true;
    })
    .map((item) => item.value as string);
}

// Tells the model which of the images is the realtor logo and to copy it exactly (see getRealtorLogoPrompt).
function realtorLogoPromptFor(templateInputs: TemplateInputValue[], imageUrls: string[]): string {
  const logo = templateInputs.find((item) => item.input.nameInPrompt === "realtor_logo")?.value;
  const logoImageNumber = typeof logo === "string" && logo.trim() ? imageUrls.indexOf(logo) + 1 : 0;
  return logoImageNumber > 0 ? getRealtorLogoPrompt(logoImageNumber) : "";
}

export default function CreativesGeneratorModal({
  open,
  onClose,
  listingId,
  editMode,
  onGenerated
}: CreativesGeneratorModalProps): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const { userSettings } = useFirestore();
  const { generateCreative } = useWebotServer();
  const { user } = useAuth();
  // Quality (resolution) is remembered per creative type in localStorage; price scales with it.
  const [creativePrefs, updateCreativePrefs] = useCreativePreferences("image", { quality: DEFAULT_CREATIVE_QUALITY });
  const quality: MakeoverQuality = creativePrefs.quality;

  const [currentStep, setCurrentStep] = useState<GenerationStep>("template_selection");
  const [selectedTemplate, setSelectedTemplate] = useState<templateDataType | null>(null);
  const [selectedListings, setSelectedListings] = useState<SelectedListings>({});
  const [templateInputs, setTemplateInputs] = useState<TemplateInputValue[]>([]);
  const [preloadedListing, setPreloadedListing] = useState<ProcessedListingData | null>(null);
  const [realtorImageAlignment, setRealtorImageAlignment] = useState<"left" | "right">("left");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [generatedImageUrls, setGeneratedImageUrls] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState<Record<number, boolean>>({});
  const [feedbackText, setFeedbackText] = useState("");
  const [isIterating, setIsIterating] = useState(false);
  const [iterationError, setIterationError] = useState<string | null>(null);
  const [showBackgroundModal, setShowBackgroundModal] = useState(false);
  const [hasAttemptedGenerate, setHasAttemptedGenerate] = useState(false);

  // Fix realtor image/logo options (for edit mode only)
  const [fixRealtorImage, setFixRealtorImage] = useState(false);
  const [fixRealtorLogo, setFixRealtorLogo] = useState(false);

  // Edit count limit - max 2 edits per creative (persisted on server)
  const MAX_EDITS = 2;
  const [editCount, setEditCount] = useState(0);
  const hasReachedEditLimit = editCount >= MAX_EDITS;

  // Listing image picker state
  const [showListingImagePicker, setShowListingImagePicker] = useState(false);
  const [listingImagePickerData, setListingImagePickerData] = useState<{
    inputIndex: number;
    images: string[];
  } | null>(null);

  // Helper function to check if we're within 60 minutes of creation
  const isWithinEditWindow = (createdTime: Date): boolean => {
    const now = new Date();
    const diffInMinutes = (now.getTime() - createdTime.getTime()) / (1000 * 60);
    return diffInMinutes <= 60;
  };

  // Helper function to get remaining edit time in seconds
  const getRemainingEditTimeSeconds = (createdTime: Date): number => {
    const now = new Date();
    const diffInSeconds = (now.getTime() - createdTime.getTime()) / 1000;
    return Math.max(0, 60 * 60 - diffInSeconds); // 60 minutes in seconds
  };
  // Helper function to format time as MM:SS
  const formatTimeRemaining = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  // State for real-time countdown
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Check if current edit is within the free edit window
  const isInFreeEditWindow = editMode?.creative?.createdTime
    ? isWithinEditWindow(editMode.creative.createdTime)
    : false;

  // Update countdown every second
  useEffect(() => {
    if (!editMode?.creative?.createdTime || !isInFreeEditWindow) {
      return;
    }

    const updateCountdown = () => {
      const seconds = getRemainingEditTimeSeconds(editMode.creative.createdTime);
      setRemainingSeconds(seconds);

      // If time is up, the component will re-render and isInFreeEditWindow will become false
      if (seconds <= 0) {
        setRemainingSeconds(0);
      }
    };

    // Initial update
    updateCountdown();

    // Set up interval for real-time updates
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [editMode?.creative?.createdTime, isInFreeEditWindow]);

  // Reset state when modal opens/closes or handle edit mode
  useEffect(() => {
    if (open) {
      if (editMode) {
        // Initialize in edit mode - go directly to result step
        setCurrentStep("result");
        setSelectedTemplate(null);
        setSelectedListings({});
        setTemplateInputs([]);
        setPreloadedListing(null);
        setRealtorImageAlignment("left");
        setIsGenerating(false);
        setGenerationError(null);
        setGeneratedImageUrls([editMode.imageUrl]);
        setUploadingImages({});
        setFeedbackText("");
        setIsIterating(false);
        setIterationError(null);
        setShowBackgroundModal(false);
        setEditCount(editMode.creative.editCount ?? 0);
      } else {
        // Normal new creation flow
        setCurrentStep("template_selection");
        setSelectedTemplate(null);
        setSelectedListings({});
        setTemplateInputs([]);
        setPreloadedListing(null);
        setRealtorImageAlignment("left");
        setIsGenerating(false);
        setGenerationError(null);
        setGeneratedImageUrls([]);
        setUploadingImages({});
        setFeedbackText("");
        setIsIterating(false);
        setIterationError(null);
        setShowBackgroundModal(false);
        setEditCount(0);
        setHasAttemptedGenerate(false);
      }
    }
  }, [open, editMode]);

  // Preload listing when listingId is provided (fetch once when modal opens).
  useEffect(() => {
    if (!listingId || !open || editMode) return;
    let cancelled = false;
    fetchListingById(listingId).then((hit) => {
      if (cancelled || !hit) return;
      setPreloadedListing(hit as unknown as ProcessedListingData);
    });
    return () => {
      cancelled = true;
    };
  }, [listingId, open, editMode]);

  // Apply preloaded listing when template is selected
  useEffect(() => {
    if (selectedTemplate && preloadedListing) {
      const listingInput = selectedTemplate.inputs.find((input) => input.type === "listing");
      if (listingInput) {
        setSelectedListings((prev) => ({
          ...prev,
          [listingInput.nameInPrompt || ""]: preloadedListing
        }));
      }
    }
  }, [selectedTemplate, preloadedListing]);

  // Initialize template inputs when template is selected or listings change
  useEffect(() => {
    if (selectedTemplate && userSettings) {
      const realtorData = {
        ownerFullName: userSettings.ownerFullName,
        ownerPhoneNumber: userSettings.ownerPhoneNumber,
        userImageUrl: userSettings.userImageUrl,
        companyLogoUrl: userSettings.companyLogoUrl
      };

      const inputs: TemplateInputValue[] = selectedTemplate.inputs.map((input) => {
        // Use ?? rather than || so that defaultValue returning `false`, `0`, or `""` is preserved.
        // Without this, a boolean toggle defaulting to `false` would get coerced to `""`, which
        // then reads as "not false" downstream and silently flips the toggle back on.
        const computedDefault =
          input.defaultValue?.({
            realtorData,
            selectedListings
          }) ?? "";
        return {
          input,
          value: computedDefault,
          defaultComputedValue: computedDefault
        };
      });

      setTemplateInputs(inputs);
    }
  }, [selectedTemplate, selectedListings, userSettings]);

  const handleTemplateSelect = (template: templateDataType) => {
    setSelectedTemplate(template);
    setCurrentStep("customization");
  };

  const handleInputChange = (inputIndex: number, value: string | boolean) => {
    setTemplateInputs((prev) =>
      prev.map((item, index) => (index === inputIndex ? { ...item, value } : item))
    );
  };

  // Check if all required inputs are filled (used for button disabled state)
  const areRequiredFieldsFilled = () =>
    templateInputs.every((item) => {
      const isRequired = item.input.required !== false;
      if (!isRequired) return true;
      if (item.input.type === "listing") return true;
      if (item.input.type === "boolean") return true;
      return typeof item.value === "string" && item.value.trim() !== "";
    });

  // Full validation including placeholder check (used in handleGenerate)
  const isFormValid = () => {
    if (!areRequiredFieldsFilled()) return false;
    // Block generation if any text input contains placeholder "___"
    const hasPlaceholders = templateInputs.some(
      (item) =>
        item.input.type === "text" && typeof item.value === "string" && item.value.includes("___")
    );
    return !hasPlaceholders;
  };

  // Validation helpers for placeholder and default value checks
  const hasPlaceholderValue = (item: TemplateInputValue): boolean =>
    item.input.type === "text" && typeof item.value === "string" && item.value.includes("___");

  const isUnchangedDefault = (item: TemplateInputValue): boolean => {
    if (item.input.type !== "text") return false;
    if (typeof item.value !== "string" || typeof item.defaultComputedValue !== "string")
      return false;
    if (!item.defaultComputedValue || item.defaultComputedValue.trim() === "") return false;
    // Don't show warning if it already has a placeholder error
    if (item.value.includes("___")) return false;
    return item.value === item.defaultComputedValue;
  };

  const hasPlaceholderInputs = templateInputs.some(hasPlaceholderValue);
  const hasUnchangedDefaultInputs = templateInputs.some(isUnchangedDefault);

  // Handle image upload
  const handleImageUpload = async (inputIndex: number, file: File) => {
    if (!user?.email) return;

    setUploadingImages((prev) => ({ ...prev, [inputIndex]: true }));

    try {
      const timestamp = new Date().getTime();
      const fileName = `creative_${timestamp}_${file.name}`;
      const imageRef = storage.ref(`creative-images/${user.email}/${fileName}`);

      // Upload the file
      const snapshot = await imageRef.put(file);
      const downloadURL = await snapshot.ref.getDownloadURL();

      // Update the input value
      handleInputChange(inputIndex, downloadURL);
    } catch (error) {
      console.error("Image upload error:", error);
    } finally {
      setUploadingImages((prev) => ({ ...prev, [inputIndex]: false }));
    }
  };

  // Handle image removal
  const handleImageRemove = (inputIndex: number) => {
    handleInputChange(inputIndex, "");
  };

  // Handle showing listing image picker
  const handleShowListingImagePicker = (inputIndex: number, images: string[]) => {
    setListingImagePickerData({ inputIndex, images });
    setShowListingImagePicker(true);
  };

  // Handle selecting image from listing
  const handleSelectListingImage = (imageUrl: string) => {
    if (listingImagePickerData) {
      handleInputChange(listingImagePickerData.inputIndex, imageUrl);
    }
    setShowListingImagePicker(false);
    setListingImagePickerData(null);
  };

  const handleGenerate = async () => {
    setHasAttemptedGenerate(true);
    if (!selectedTemplate || !userSettings || !isFormValid()) return;

    // For new creative generation (not edit mode), show background modal and close main modal
    if (!editMode) {
      setIsGenerating(true);
      setGenerationError(null);

      try {
        const realtorData = {
          ownerFullName: userSettings.ownerFullName,
          ownerPhoneNumber: userSettings.ownerPhoneNumber,
          userImageUrl: userSettings.userImageUrl,
          companyLogoUrl: userSettings.companyLogoUrl
        };

        const promptParams: promptGenerationFinalParams = {
          promptGeneratorInitialParams: {
            realtorData,
            selectedListings
          },
          realtorImageAlignement: realtorImageAlignment,
          templateFinalInputs: templateInputs.map((item) => ({
            ...item.input,
            finalValue: item.value
          }))
        };

        const imageUrls = collectImageUrls(templateInputs);
        const prompt =
          selectedTemplate.promptGenerator(promptParams) +
          realtorLogoPromptFor(templateInputs, imageUrls);

        // Start the generation in the background. Coins are consumed server-side at
        // the start of the request, so refresh the balance once it resolves.
        generateCreative({
          prompt,
          imageUrls,
          listingIds: computeListingIds(selectedListings, listingId),
          quality
        })
          .then(() => onGenerated?.())
          .catch((error) => console.error("Background creative generation error:", error));

        // Close the main modal and show background modal
        onClose();
        setShowBackgroundModal(true);

        // Auto-close background modal after 4 seconds
        setTimeout(() => {
          setShowBackgroundModal(false);
        }, 4000);
      } catch (error) {
        console.error("Error starting creative generation:", error);
        setGenerationError(t("creativesGenerator.networkError", "Network error occurred"));
      } finally {
        setIsGenerating(false);
      }
    } else {
      // For edit mode (iterations), keep the existing behavior
      setIsGenerating(true);
      setGenerationError(null);
      setCurrentStep("generating");

      try {
        const realtorData = {
          ownerFullName: userSettings.ownerFullName,
          ownerPhoneNumber: userSettings.ownerPhoneNumber,
          userImageUrl: userSettings.userImageUrl,
          companyLogoUrl: userSettings.companyLogoUrl
        };

        const promptParams: promptGenerationFinalParams = {
          promptGeneratorInitialParams: {
            realtorData,
            selectedListings
          },
          realtorImageAlignement: realtorImageAlignment,
          templateFinalInputs: templateInputs.map((item) => ({
            ...item.input,
            finalValue: item.value
          }))
        };

        const imageUrls = collectImageUrls(templateInputs);
        const prompt =
          selectedTemplate.promptGenerator(promptParams) +
          realtorLogoPromptFor(templateInputs, imageUrls);

        const response = await generateCreative({
          prompt,
          imageUrls,
          listingIds: computeListingIds(selectedListings, listingId),
          quality
        });

        if (response.success && response.generatedImageUrls) {
          setGeneratedImageUrls(response.generatedImageUrls);
          setCurrentStep("result");
        } else if (response.errorCode === "insufficient_coins") {
          setGenerationError(
            t(
              "creativesGenerator.insufficientCoins",
              "אין מספיק טוקנים ליצירת קריאטיב. ניתן לרכוש טוקנים נוספים בפנייה לתמיכה."
            )
          );
          setCurrentStep("customization");
        } else {
          setGenerationError(
            response.errorMessage || t("creativesGenerator.unknownError", "Unknown error occurred")
          );
          setCurrentStep("customization");
        }
        onGenerated?.();
      } catch (error) {
        console.error("Error generating creative:", error);
        setGenerationError(t("creativesGenerator.networkError", "Network error occurred"));
        setCurrentStep("customization");
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === "customization") {
      setCurrentStep("template_selection");
      setHasAttemptedGenerate(false);
    } else if (currentStep === "result") {
      setCurrentStep("customization");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleIterate = async () => {
    // Allow iteration if either feedback text is provided OR fix options are selected
    const hasFeedback = feedbackText.trim() !== "";
    const hasFixOptions = fixRealtorImage || fixRealtorLogo;

    if ((!hasFeedback && !hasFixOptions) || generatedImageUrls.length === 0) return;

    setIsIterating(true);
    setIterationError(null);

    try {
      // Use the first generated image for iteration
      const originalImageUrl = generatedImageUrls[0];

      // Build the prompt with fix instructions if needed
      let fullPrompt = feedbackText.trim();

      // Add fix realtor image instruction if selected
      if (fixRealtorImage) {
        fullPrompt +=
          "\n\nReplace the realtor in the image (if any) with the cut-out (background removed) image of the realtor provided";
      }

      // Build image URLs array
      const imageUrls = [originalImageUrl];

      // Add realtor image if fix is selected
      if (fixRealtorImage && userSettings?.userImageUrl) {
        imageUrls.push(userSettings.userImageUrl);
      }

      // Add company logo if fix is selected — it is the last image, and the instruction names it by number
      if (fixRealtorLogo && userSettings?.companyLogoUrl) {
        imageUrls.push(userSettings.companyLogoUrl);
        fullPrompt += "\n\n" + getFixRealtorLogoPrompt(imageUrls.length);
      }

      const response = await generateCreative({
        prompt: fullPrompt,
        imageUrls: imageUrls,
        // Include originalCreativeId for edit requests
        originalCreativeId: editMode?.creative?.id,
        listingIds: computeListingIds(selectedListings, listingId),
        quality
      });

      if (response.success && response.generatedImageUrls) {
        setGeneratedImageUrls(response.generatedImageUrls);
        setFeedbackText(""); // Clear the feedback text after successful iteration
        setEditCount((prev) => prev + 1);

        // Reset fix options after successful edit
        setFixRealtorImage(false);
        setFixRealtorLogo(false);

        // Replace the current creative with the new generated one
        if (editMode?.creative) {
          const newEditCount = (editMode.creative.editCount ?? 0) + 1;
          // Create a new creative object with updated timestamp for the new edit window
          const newCreative = {
            ...editMode.creative,
            id: response.generatedCreativeId || editMode.creative.id,
            createdTime: new Date(), // Reset the creation time for new 60-minute window
            generatedCreativeUrl: response.generatedImageUrls[0],
            editCount: newEditCount
          };

          // Update editMode to use the new creative as the original
          editMode.creative = newCreative;
          editMode.imageUrl = response.generatedImageUrls[0];
        }
      } else {
        setIterationError(
          response.errorMessage || t("creativesGenerator.iterateError", "Error improving creative")
        );
      }
    } catch (error) {
      console.error("Error iterating creative:", error);
      setIterationError(t("creativesGenerator.iterateError", "Error improving creative"));
    } finally {
      setIsIterating(false);
    }
  };

  const handleDownloadImage = async (imageUrl: string) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(imageUrl);
      const blob = await response.blob();

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `creative-${new Date().toISOString()}.jpg`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading image:", error);
      // Fallback to opening in new tab if download fails
      window.open(imageUrl, "_blank");
    }
  };

  const renderImageInput = (item: TemplateInputValue, inputIndex: number) => {
    const isUploading = uploadingImages[inputIndex];
    const hasImage = typeof item.value === "string" && item.value.trim() !== "";
    const isRequired = item.input.required !== false; // Default to true if not specified

    // Check if this is a listing image input
    const isListingImageData = (data: any): data is ListingImageAdditionalData => {
      return data && ("imageSourceHint" in data || "listingNameInPrompt" in data);
    };

    const listingImageData =
      item.input.additionalData && isListingImageData(item.input.additionalData)
        ? item.input.additionalData
        : null;

    const hasListingImageHint = listingImageData?.imageSourceHint === "listing_images";
    const listingNameInPrompt = listingImageData?.listingNameInPrompt || "main_listing";

    // Get the selected listing for this input
    const selectedListing = selectedListings[listingNameInPrompt];
    const listingImages = selectedListing?.imageUrls || [];
    const hasListingImages = listingImages.length > 0;

    // Show listing image picker if we have listing images and this is a listing image input
    const showListingImagePicker = hasListingImageHint && hasListingImages;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {item.input.label}
          {isRequired ? " *" : ""}
        </Typography>

        {hasImage ? (
          // Show image preview with remove option
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={typeof item.value === "string" ? item.value : ""}
              sx={{
                width: 80,
                height: 80,
                borderRadius: 1,
                bgcolor: "grey.200"
              }}
              variant="square"
            >
              <ImageIcon />
            </Avatar>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {item.input.description}
              </Typography>
              <Button
                size="small"
                color="secondary"
                onClick={() => handleImageRemove(inputIndex)}
                startIcon={<DeleteIcon />}
              >
                הסר תמונה
              </Button>
              {/* Show listing image picker button even when there's already an image */}
              {showListingImagePicker && (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => {
                    // We'll implement this function
                    handleShowListingImagePicker(inputIndex, listingImages);
                  }}
                  startIcon={<PhotoLibraryIcon />}
                  sx={{ ml: 1 }}
                >
                  בחר מתמונות הנכס
                </Button>
              )}
            </Box>
          </Box>
        ) : (
          // Show upload button and listing image picker when no image
          <Box>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              {/* Upload button */}
              <Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id={`image-upload-input-${inputIndex}`}
                  type="file"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleImageUpload(inputIndex, file);
                    }
                  }}
                />
                <label htmlFor={`image-upload-input-${inputIndex}`}>
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={isUploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={isUploading}
                  >
                    {isUploading ? "מעלה..." : "העלאת תמונה"}
                  </Button>
                </label>
              </Box>

              {/* Listing image picker button */}
              {showListingImagePicker && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    handleShowListingImagePicker(inputIndex, listingImages);
                  }}
                  startIcon={<PhotoLibraryIcon />}
                  disabled={isUploading}
                >
                  בחר מתמונות הנכס
                </Button>
              )}
            </Box>

            <Typography variant="caption" display="block" color="text.secondary">
              {item.input.description}
            </Typography>

            {/* Show available listing images count */}
            {showListingImagePicker && (
              <Typography variant="caption" display="block" color="primary.main" sx={{ mt: 0.5 }}>
                זמינות {listingImages.length} תמונות מהנכס הנבחר
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const renderTextField = (item: TemplateInputValue, inputIndex: number) => {
    const isRequired = item.input.required !== false; // Default to true if not specified
    const showPlaceholderError = hasAttemptedGenerate && hasPlaceholderValue(item);
    const showUnchangedWarning = hasAttemptedGenerate && isUnchangedDefault(item);

    return (
      <>
        <TextField
          label={item.input.label}
          value={item.value}
          onChange={(e) => handleInputChange(inputIndex, e.target.value)}
          helperText={item.input.description}
          multiline={item.input.type === "text"}
          rows={item.input.type === "text" ? 2 : 1}
          required={isRequired}
          error={showPlaceholderError}
          sx={{ mb: showPlaceholderError || showUnchangedWarning ? 0.5 : 2 }}
        />
        {showPlaceholderError && (
          <Typography variant="caption" color="error" sx={{ mb: 2, display: "block" }}>
            {t("creativesGenerator.placeholderError", "יש להחליף את הערכים הריקים (___)")}
          </Typography>
        )}
        {showUnchangedWarning && (
          <Typography variant="caption" color="warning.main" sx={{ mb: 2, display: "block" }}>
            {t(
              "creativesGenerator.unchangedDefaultWarning",
              "שים לב - ערך זה לא שונה מברירת המחדל"
            )}
          </Typography>
        )}
      </>
    );
  };

  const renderColorInput = (item: TemplateInputValue, inputIndex: number) => {
    // Type guard to check if additionalData is ColorAdditionalData
    const isColorData = (data: any): data is ColorAdditionalData => {
      return (
        data &&
        ("predefinedColors" in data || "allowCustomText" in data || "customTextPlaceholder" in data)
      );
    };

    const colorData =
      item.input.additionalData && isColorData(item.input.additionalData)
        ? item.input.additionalData
        : {};
    const predefinedColors = colorData.predefinedColors || [];
    const allowCustomText = colorData.allowCustomText || false;
    const customTextPlaceholder = colorData.customTextPlaceholder || "";
    const isRequired = item.input.required !== false; // Default to true if not specified

    // Check if the current value is one of the predefined colors
    const isPresetColor = predefinedColors.some((color) => color.value === item.value);
    const isCustomColor =
      !isPresetColor && typeof item.value === "string" && item.value.trim() !== "";

    const handleDropdownChange = (value: string) => {
      handleInputChange(inputIndex, value);
    };

    return (
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth sx={{ mb: 1 }}>
          <InputLabel>
            {item.input.label}
            {isRequired ? " *" : ""}
          </InputLabel>
          <Select
            value={isCustomColor ? "custom" : item.value}
            onChange={(e) => handleDropdownChange(e.target.value as string)}
            label={item.input.label}
            renderValue={(selected) => {
              if (selected === "custom") {
                return t("creativesGenerator.customColor", "צבע מותאם אישית");
              }
              const selectedColor = predefinedColors.find((color) => color.value === selected);
              return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {selectedColor && (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        bgcolor: selectedColor.preview,
                        border: 1,
                        borderColor: "grey.400",
                        borderRadius: "50%",
                        flexShrink: 0
                      }}
                    />
                  )}
                  {selectedColor?.name || selected}
                </Box>
              );
            }}
          >
            {predefinedColors.map((color, colorIndex) => (
              <MenuItem key={colorIndex} value={color.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      bgcolor: color.preview,
                      border: 1,
                      borderColor: "grey.400",
                      borderRadius: "50%",
                      flexShrink: 0
                    }}
                  />
                  {color.name}
                </Box>
              </MenuItem>
            ))}
            {allowCustomText && (
              <MenuItem value="custom">
                {t("creativesGenerator.customColor", "צבע מותאם אישית")}
              </MenuItem>
            )}
          </Select>
        </FormControl>

        {/* Custom Text Input - Show when "custom" is selected */}
        {(isCustomColor || item.value === "custom") && allowCustomText && (
          <TextField
            fullWidth
            value={isCustomColor ? item.value : ""}
            onChange={(e) => handleInputChange(inputIndex, e.target.value)}
            placeholder={customTextPlaceholder}
            variant="outlined"
            size="small"
            label={t("creativesGenerator.enterCustomColor", "הזינו צבע מותאם")}
            sx={{ mb: 1 }}
          />
        )}

        <Typography variant="caption" display="block" color="text.secondary">
          {item.input.description}
        </Typography>
      </Box>
    );
  };

  const renderBooleanInput = (item: TemplateInputValue, inputIndex: number) => (
    <Box sx={{ mb: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={item.value === true}
            onChange={(e) => handleInputChange(inputIndex, e.target.checked)}
          />
        }
        label={item.input.label}
      />
      <Typography variant="caption" display="block" color="text.secondary">
        {item.input.description}
      </Typography>
    </Box>
  );

  const renderListingInput = (item: TemplateInputValue, inputIndex: number) => {
    const selectedListing = selectedListings[item.input.nameInPrompt || ""];
    const isRequired = item.input.required !== false; // Default to true if not specified

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {item.input.label}
          {isRequired ? " *" : ""}
        </Typography>
        <ListingPicker
          selectedListing={selectedListing as ListingData | null}
          setSelectedListing={(listing: ListingData | null) => {
            setSelectedListings((prev) => ({
              ...prev,
              [item.input.nameInPrompt || ""]: listing as ProcessedListingData | null
            }));
          }}
        />
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          {item.input.description}
        </Typography>
      </Box>
    );
  };

  const getStepContent = () => {
    switch (currentStep) {
      case "template_selection":
        return (
          <Box>
            {/* Explanatory text */}
            <Box sx={{ mb: 4, p: 3, bgcolor: "primary.lighter", borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                איך זה עובד?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                בחרו תבנית, התאימו אותה לנכס שלכם ולפרטים האישיים, ותוכלו ליצור קריאטיב מקצועי
                לרשתות החברתיות תוך דקות ספורות.
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {creativeTemplates.map((template, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "transform 0.2s",
                      "&:hover": {
                        transform: "scale(1.02)"
                      }
                    }}
                    onClick={() => handleTemplateSelect(template)}
                  >
                    <Box sx={{ position: "relative", width: "100%", paddingTop: "100%" }}>
                      <Image
                        src={template.displayImageUrl}
                        alt={template.templateName}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {template.templateName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {template.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      case "customization":
        const templateSpecificInputs = templateInputs.filter(
          (item) => item.input.showInSection === "template_specific"
        );
        const includeRealtorImageToggle = templateInputs.find(
          (item) => item.input.nameInPrompt === "include_realtor_image"
        );
        const isRealtorImageIncluded = includeRealtorImageToggle?.value !== false;
        const realtorInfoInputs = templateInputs.filter((item) => {
          if (item.input.showInSection !== "realtor_info") return false;
          // Hide the realtor image picker when the include toggle is off
          if (!isRealtorImageIncluded && item.input.nameInPrompt === "realtor_image") return false;
          return true;
        });
        const listingInfoInputs = templateInputs.filter(
          (item) => item.input.showInSection === "listing_info"
        );

        return (
          <Grid container spacing={4}>
            {/* Template Display Image - Right side on desktop, top on mobile */}
            <Grid item xs={12} md={4} order={{ xs: 1, md: 2 }}>
              {selectedTemplate && (
                <Box sx={{ position: "sticky", top: 20 }}>
                  <Typography variant="h6" gutterBottom>
                    {t("creativesGenerator.templatePreview", "תצוגה מקדימה של התבנית")}
                  </Typography>
                  <Card>
                    <Box sx={{ position: "relative", width: "100%", paddingTop: "100%" }}>
                      <Image
                        src={selectedTemplate.displayImageUrl}
                        alt={selectedTemplate.templateName}
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover"
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography variant="body2" color="textSecondary">
                        {selectedTemplate.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}
            </Grid>

            {/* Form Content - Left side on desktop, bottom on mobile */}
            <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
              <Box>
                {generationError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {generationError}
                  </Alert>
                )}

                {/* Listing Info Inputs - First */}
                {listingInfoInputs.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("creativesGenerator.listingInfo", "פרטי נכס")}
                    </Typography>
                    <Grid container spacing={2}>
                      {listingInfoInputs.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          {item.input.type === "listing"
                            ? renderListingInput(item, templateInputs.indexOf(item))
                            : item.input.type === "image"
                            ? renderImageInput(item, templateInputs.indexOf(item))
                            : renderTextField(item, templateInputs.indexOf(item))}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}

                {/* Template Specific Inputs - Second */}
                {templateSpecificInputs.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("creativesGenerator.templateSettings", "הגדרות תבנית")}
                    </Typography>
                    <Grid container spacing={2}>
                      {templateSpecificInputs.map((item, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          {item.input.type === "image"
                            ? renderImageInput(item, templateInputs.indexOf(item))
                            : item.input.type === "color"
                            ? renderColorInput(item, templateInputs.indexOf(item))
                            : item.input.type === "boolean"
                            ? renderBooleanInput(item, templateInputs.indexOf(item))
                            : renderTextField(item, templateInputs.indexOf(item))}
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                )}

                {/* Realtor Info Inputs - Third */}
                {realtorInfoInputs.length > 0 && (
                  <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("creativesGenerator.realtorInfo", "פרטי מתווך")}
                    </Typography>
                    <Grid container spacing={2}>
                      {realtorInfoInputs.map((item, index) => {
                        const isRealtorImageItem = item.input.nameInPrompt === "realtor_image";
                        return (
                          <Fragment key={index}>
                            <Grid item xs={12} sm={6}>
                              {item.input.type === "image"
                                ? renderImageInput(item, templateInputs.indexOf(item))
                                : item.input.type === "boolean"
                                ? renderBooleanInput(item, templateInputs.indexOf(item))
                                : renderTextField(item, templateInputs.indexOf(item))}
                            </Grid>
                            {/* Realtor image alignment lives right next to the realtor image picker */}
                            {isRealtorImageItem && isRealtorImageIncluded && (
                              <Grid item xs={12} sm={6}>
                                <FormControl fullWidth sx={{ mb: 2 }}>
                                  <InputLabel>
                                    {t(
                                      "creativesGenerator.realtorImageAlignment",
                                      "מיקום תמונת המתווך"
                                    )}
                                  </InputLabel>
                                  <Select
                                    value={realtorImageAlignment}
                                    onChange={(e) =>
                                      setRealtorImageAlignment(e.target.value as "left" | "right")
                                    }
                                    label={t(
                                      "creativesGenerator.realtorImageAlignment",
                                      "מיקום תמונת המתווך"
                                    )}
                                  >
                                    <MenuItem value="left">
                                      {t("creativesGenerator.left", "שמאל")}
                                    </MenuItem>
                                    <MenuItem value="right">
                                      {t("creativesGenerator.right", "ימין")}
                                    </MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                          </Fragment>
                        );
                      })}
                    </Grid>
                  </Paper>
                )}
              </Box>
            </Grid>
          </Grid>
        );

      case "generating":
        return (
          <Box display="flex" flexDirection="column" alignItems="center" py={8}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              {t("creativesGenerator.generating", "יוצר קריאטיב...")}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t("creativesGenerator.pleaseWait", "אנא המתן, זה עשוי לקחת מספר דקות")}
            </Typography>
          </Box>
        );

      case "result":
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {t("creativesGenerator.resultTitle", "הקריאטיב שלך מוכן!")}
            </Typography>

            <Grid container spacing={4} sx={{ height: "100%" }}>
              {/* Image Display */}
              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                {generatedImageUrls.map((url, index) => (
                  <Card
                    key={index}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      height: "65vh",
                      width: "100%"
                    }}
                  >
                    {/* Square image container - 1:1 aspect ratio */}
                    <Box
                      sx={{
                        p: 2,
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          width: "55vh",
                          height: "55vh",
                          maxWidth: "100%",
                          maxHeight: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                      >
                        <Image
                          src={url}
                          alt={`Generated Creative ${index + 1}`}
                          key={url}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            width: "auto",
                            height: "auto",
                            objectFit: "contain"
                          }}
                        />
                        {/* Loading overlay during iteration */}
                        {isIterating && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundColor: "rgba(0, 0, 0, 0.6)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              borderRadius: 1
                            }}
                          >
                            <Box sx={{ textAlign: "center", color: "white" }}>
                              <CircularProgress sx={{ color: "white", mb: 2 }} />
                              <Typography variant="body2" sx={{ color: "white" }}>
                                {t("creativesGenerator.iterating", "משפר את הקריאטיב...")}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                    {/* Download button - always at bottom */}
                    <CardContent sx={{ flexShrink: 0, pt: 0 }}>
                      <Button fullWidth variant="outlined" onClick={() => handleDownloadImage(url)}>
                        {t("creativesGenerator.download", "הורד")}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Grid>

              {/* Iteration Panel */}
              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                <Box
                  sx={{
                    p: 3,
                    height: "65vh",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  <Typography variant="h6" gutterBottom>
                    {t("creativesGenerator.iterateTitle", "שיפור הקריאטיב")}
                  </Typography>

                  {/* Edit Info Chips */}
                  <Box sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
                      <Chip
                        label={t(
                          "creativesGenerator.editsRemaining",
                          "{{remaining}} עריכות נותרו",
                          { remaining: MAX_EDITS - editCount }
                        )}
                        color={hasReachedEditLimit ? "error" : "default"}
                        variant="outlined"
                        size="small"
                      />
                      {editMode && isInFreeEditWindow && (
                        <Chip
                          label={t(
                            "creativesGenerator.freeEditTimeCountdown",
                            "זמן נותר לעריכה חינם - {{time}}",
                            { time: formatTimeRemaining(remainingSeconds) }
                          )}
                          color="success"
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Stack>
                    {editMode && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {isInFreeEditWindow
                          ? t(
                              "creativesGenerator.freeEditExplanation",
                              "בשעה הראשונה לאחר יצירת הקריאטיב, עריכות לא נחשבות במכסה החודשית"
                            )
                          : t(
                              "creativesGenerator.paidEditExplanation",
                              "עריכות לאחר 60 דקות מהיצירה נחשבות כקריאטיב חדש במכסה החודשית"
                            )}
                      </Typography>
                    )}
                  </Box>

                  {/* Fix Realtor Image/Logo Section - Only in edit mode and within edit limit */}
                  {editMode && userSettings && !hasReachedEditLimit && (
                    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
                      <Typography variant="subtitle1" gutterBottom>
                        {t("creativesGenerator.fixRealtorSection", "תיקון תמונה אישית או לוגו")}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {t(
                          "creativesGenerator.fixRealtorSectionDescription",
                          "בחרו באפשרויות הבאות רק אם התמונה האישית או הלוגו לא הוצגו בצורה נכונה בקריאטיב"
                        )}
                      </Typography>

                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {/* Fix Realtor Image Option */}
                        {userSettings.userImageUrl && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={fixRealtorImage}
                                onChange={(e) => setFixRealtorImage(e.target.checked)}
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  src={userSettings.userImageUrl}
                                  sx={{ width: 24, height: 24 }}
                                />
                                <Typography variant="body2">
                                  {t("creativesGenerator.fixRealtorImage", "תיקון תמונה אישית")}
                                </Typography>
                              </Box>
                            }
                          />
                        )}

                        {/* Fix Realtor Logo Option */}
                        {userSettings.companyLogoUrl && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={fixRealtorLogo}
                                onChange={(e) => setFixRealtorLogo(e.target.checked)}
                              />
                            }
                            label={
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Avatar
                                  src={userSettings.companyLogoUrl}
                                  sx={{ width: 24, height: 24 }}
                                  variant="square"
                                />
                                <Typography variant="body2">
                                  {t("creativesGenerator.fixRealtorLogo", "תיקון לוגו החברה")}
                                </Typography>
                              </Box>
                            }
                          />
                        )}
                      </Box>
                    </Paper>
                  )}

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t(
                      "creativesGenerator.iterateInstructions",
                      "תוכלו לבקש שינויים קלים בתמונה על ידי כתיבת תיאור מדויק ופרטני. היו ספציפיים ככל האפשר - למשל: 'שנה את צבע הרקע לכחול', 'הוסף טקסט בגודל גדול יותר', או 'שנה את התאורה להיות יותר בהירה'."
                    )}
                  </Typography>

                  {iterationError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {iterationError}
                    </Alert>
                  )}

                  {hasReachedEditLimit ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      {t(
                        "creativesGenerator.editLimitReached",
                        "הגעת למקסימום {{max}} עריכות לקריאטיב זה",
                        { max: MAX_EDITS }
                      )}
                    </Alert>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={feedbackText}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        placeholder={t(
                          "creativesGenerator.feedbackPlaceholder",
                          "תארו את השינוי שברצונכם לבצע בתמונה..."
                        )}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        disabled={isIterating}
                      />

                      <LoadingButton
                        fullWidth
                        variant="contained"
                        onClick={handleIterate}
                        disabled={
                          (!feedbackText.trim() && !fixRealtorImage && !fixRealtorLogo) ||
                          isIterating
                        }
                        loading={isIterating}
                        loadingPosition="start"
                      >
                        {isIterating
                          ? t("creativesGenerator.iterating", "משפר את הקריאטיב...")
                          : t("creativesGenerator.regenerate", "יצירה מחדש")}
                      </LoadingButton>
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "template_selection":
        return t("creativesGenerator.selectTemplate", "בחירת תבנית");
      case "customization":
        return t("creativesGenerator.customize", "התאמה אישית");
      case "generating":
        return t("creativesGenerator.generating", "יוצר קריאטיב...");
      case "result":
        return t("creativesGenerator.result", "התוצאה");
      default:
        return "";
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {(currentStep === "customization" || currentStep === "result") && (
                <IconButton onClick={handleBack} sx={{ mr: 1 }}>
                  <ArrowBackIcon
                    sx={{ transform: theme.direction === "rtl" ? "rotate(180deg)" : "none" }}
                  />
                </IconButton>
              )}
              <Typography variant="h6">{getStepTitle()}</Typography>
            </Box>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ minHeight: 400 }}>{getStepContent()}</DialogContent>

        <DialogActions>
          {currentStep === "customization" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
              <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                {hasAttemptedGenerate && hasPlaceholderInputs && (
                  <Alert severity="error" sx={{ py: 0 }}>
                    {t(
                      "creativesGenerator.placeholderErrorTip",
                      "יש שדות עם ערכים ריקים (___) שצריך למלא לפני יצירת הקריאטיב"
                    )}
                  </Alert>
                )}
                {hasAttemptedGenerate && hasUnchangedDefaultInputs && (
                  <Alert severity="warning" sx={{ py: 0 }}>
                    {t(
                      "creativesGenerator.unchangedDefaultWarningTip",
                      "יש שדות שלא שונו מברירת המחדל - מומלץ לבדוק ולהתאים אישית"
                    )}
                  </Alert>
                )}
              </Box>
              <CreativeQualitySelector quality={quality} onChange={(q) => updateCreativePrefs({ quality: q })} />
              <LoadingButton
                loading={isGenerating}
                variant="contained"
                onClick={handleGenerate}
                disabled={!areRequiredFieldsFilled()}
              >
                {t("creativesGenerator.generate", "יצירת קריאטיב")}
              </LoadingButton>
            </Box>
          )}
          {currentStep === "result" && (
            <Button variant="contained" onClick={handleClose}>
              {t("common.close", "סגור")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <CreativeGeneratingBackgroundModal
        open={showBackgroundModal}
        onClose={() => setShowBackgroundModal(false)}
      />

      {/* Listing Image Picker Modal */}
      <Dialog
        open={showListingImagePicker}
        onClose={() => setShowListingImagePicker(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">בחר תמונה מהנכס</Typography>
            <IconButton onClick={() => setShowListingImagePicker(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {listingImagePickerData?.images.map((imageUrl, index) => (
              <Grid item xs={6} sm={4} md={3} key={index}>
                <Card
                  sx={{
                    cursor: "pointer",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: 4
                    }
                  }}
                  onClick={() => handleSelectListingImage(imageUrl)}
                >
                  <Box sx={{ position: "relative", width: "100%", paddingTop: "100%" }}>
                    <Image
                      src={imageUrl}
                      alt={`תמונת נכס ${index + 1}`}
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover"
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowListingImagePicker(false)}>ביטול</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
